import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  format,
  isBefore,
  isSameDay,
  isAfter,
  startOfDay,
  endOfDay,
} from "date-fns";
import { pl } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import orangeSortIcon from "./../../media/orange-sort-icon.svg";
import deleteButtonIcon from "./../../media/delete-button.svg";
import sortIcon from "./../../media/sort-icon.svg";
import CustomDropdown from "./customDropdowan";
import { API_URL, useAuth } from "./../../AuthContext.js";
import Logger from "../../utils/Logger";
import { useNotification } from "../../components/NotificationContext";
import { createPortal } from "react-dom";
import httpClient from "../../services/httpClient.js";
import eventBus from "../../components/eventBus.js";

const RecurringReservations = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [date, setDate] = useState(null);
  const [startHour, setStartHour] = useState(null);
  const [endHour, setEndHour] = useState(null);
  const [city, setCity] = useState("");
  const [tripPurpose, setTripPurpose] = useState("");
  const [selectedCar, setSelectedCar] = useState(null);
  const [errors, setErrors] = useState({});
  const [hoveredButton, setHoveredButton] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState(false);
  const [isDatesSet, setIsDatesSet] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [newParticipant, setNewParticipant] = useState("");
  const [confirmationData, setConfirmationData] = useState({});
  const [selectedDay, setSelectedDay] = useState("");
  const [rooms, setRooms] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [employees, setEmployees] = useState([]);
  const [newParticipantId, setNewParticipantId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // const { showNotification } = useNotification();

  const { userData } = useAuth();
  const getEmail = () => userData.userEmail;
  const getName = () => userData.userName;

  const availableWeekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  useEffect(() => {
    Logger.log(
      "useEffect wywołany! Data:",
      selectedDay,
      " Start:",
      startHour,
      " Koniec:",
      endHour,
    );
    if (selectedDay && startHour && endHour) {
      fetchRooms(selectedDay, startHour, endHour);
    }
  }, [selectedDay, startHour, endHour]);

  const fetchRooms = async (selectedDay, startTime, endTime) => {
    try {
      if (!selectedDay || !startTime || !endTime) {
        Logger.error("Nie można pobrać sal: Brak wymaganych wartości");
        return;
      }

      const formattedStartTimeUrl = encodeURIComponent(
        format(startTime, "HH:mm:ss"),
      );
      const formattedEndTimeUrl = encodeURIComponent(
        format(endTime, "HH:mm:ss"),
      );

      Logger.log(
        "Dane do API",
        selectedDay,
        formattedStartTimeUrl,
        formattedEndTimeUrl,
      );
      const response = await httpClient.get(
        `/Rooms/available-rooms/recurring?dayOfWeek=${selectedDay}&startTime=${formattedStartTimeUrl}&endTime=${formattedEndTimeUrl}`,
        { withAuth: true },
      );

      const data = response.data;

      if (!Array.isArray(data)) {
        Logger.error("Błąd: API nie zwróciło tablicy!", data);
        setRooms([]); // Ustaw pustą tablicę jako domyślną wartość
        return;
      }
      setRooms(data);

      setIsDatesSet(true);
      Logger.log("Pokoje:", data);
      Logger.log("Czy daty są ustawione:", isDatesSet);
    } catch (error) {
      Logger.error("Błąd podczas pobierania sal:", error);
    }
  };

  useEffect(() => {
    // Fetch employee list from API
    const fetchEmployees = async () => {
      try {
        const response = await httpClient.get("/Users", {
          withAuth: true,
        });
        const data = response.data;

        setEmployees(data);
      } catch (error) {
        Logger.error("Błąd przy pobieraniu uczestników", error);
      }
    };

    fetchEmployees();
  }, []);

  const options = [
    "Poniedziałek",
    "Wtorek",
    "Środa",
    "Czwartek",
    "Piątek",
    "Sobota",
    "Niedziela",
  ];

  const isDateAndTimeSet = selectedDay && startHour && endHour;

  const handleDayChange = (day) => {
    setSelectedDay(availableWeekDays[day]);
  };

  const handleCarSelection = (car) => {
    setSelectedCar(selectedCar === car ? null : car);
  };

  const handleChange = (option) => {
    setSelectedOption(option);
    const dayIndex = options.indexOf(option);
    const dayName = availableWeekDays[dayIndex];
    Logger.log("Indeks dnia:", dayIndex, "Nazwa dnia:", dayName);

    setSelectedDay(dayName);
    setDate(new Date());
    Logger.log("Wybrana opcja:", option);
  };

  const handleStartHourChange = (date) => {
    setStartHour(date);
    if (date && endHour && isSameDay(date, endHour)) {
      if (!isBefore(date, endHour)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          startHour:
            "Godzina rozpoczęcia musi być wcześniejsza niż godzina zakończenia",
        }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, startHour: null }));
      }
    }
    if (date && endHour && isBefore(date, endHour)) {
      setIsDatesSet(true);
    }
  };

  const handleEndHourChange = (date) => {
    setEndHour(date);
    if (date && startHour && isSameDay(date, startHour)) {
      if (!isBefore(startHour, date)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          endHour:
            "Godzina zakończenia musi być późniejsza niż godzina rozpoczęcia",
        }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, endHour: null }));
      }
    }
    if (date && startHour && isAfter(date, startHour)) {
      setIsDatesSet(true);
    }
  };

  const handleAddParticipant = () => {
    if (newParticipantId) {
      const selectedEmployee = employees.find(
        (emp) => emp.id === newParticipantId,
      );
      if (selectedEmployee.email === getEmail()) {
        // showNotification("Nie możesz dodać siebie jako uczestnika.", "error");
        return; // Nie dodawaj uczestnika, jeśli to użytkownik
      }
      if (selectedEmployee) {
        setParticipants([
          ...participants,
          {
            id: selectedEmployee.id,
            name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
            email: selectedEmployee.email,
          },
        ]);
      }
      setNewParticipantId(""); // Reset the selected participant
    }
  };

  const handleRemoveParticipant = (index) => {
    setParticipants(participants.filter((_, i) => i !== index));
  };

  const handleReservation = () => {
    const newErrors = {};

    if (!selectedDay) {
      newErrors.selectedDay = "Proszę wybrać datę";
    }
    if (!startHour) {
      newErrors.startHour = "Proszę wybrać godzinę rozpoczęcia";
    }
    if (!endHour) {
      newErrors.endHour = "Proszę wybrać godzinę zakończenia";
    }

    if (startHour && endHour && isSameDay(startHour, endHour)) {
      if (!isBefore(startHour, endHour)) {
        newErrors.endHour =
          "Godzina zakończenia musi być późniejsza niż godzina rozpoczęcia";
      }
    } else if (startHour && endHour && !isSameDay(startHour, endHour)) {
      newErrors.endHour =
        "Godzina zakończenia musi być w tym samym dniu co godzina rozpoczęcia";
    }

    if (!tripPurpose) {
      newErrors.tripPurpose = "Proszę podać cel spotkania";
    }

    if (!selectedCar) {
      newErrors.selectedCar = "Proszę wybrać salę";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      const reservationData = {
        dayOfWeek: selectedDay,
        fromTime: startHour ? format(startHour, "HH:mm:ss") : "",
        toTime: endHour ? format(endHour, "HH:mm:ss") : "",
        title: tripPurpose,
        organizer: getName(),
        emailAddress: getEmail(),
        conferenceRoomId: rooms.find((room) => room.name === selectedCar)?.id,
        attendees: participants.map((participant) => ({
          employee: participant.name,
          emailAddress: participant.email,
        })),
      };

      // Step 3: Show confirmation modal
      setConfirmationData({
        car: selectedCar,
        weekofDay: options[selectedDay - 1],
        startHour: format(startHour, "HH:mm"),
        endHour: format(endHour, "HH:mm"),
        participants: participants.map((p) => p.name),
        tripPurpose: tripPurpose,
      });

      setShowConfirmation(true);
    }
  };

  const handleConfirm = async () => {
    Logger.log("Rezerwacja potwierdzona");
    setIsLoading(true); // Start loading

    const reservationData = {
      roomId: rooms.find((room) => room.name === selectedCar)?.id,
      dayOfWeek: selectedDay,
      startTime: startHour ? format(startHour, "HH:mm:ss") : "",
      endTime: endHour ? format(endHour, "HH:mm:ss") : "",
      title: tripPurpose,
      participantIds: participants.map((participant) => participant.id),
    };

    Logger.log("Dane rezerwacji:", reservationData);

    try {
      const response = await httpClient.post(
        "/RecurringRoomReservations",
        reservationData,
        { withAuth: true },
      );

      // showNotification("Rezerwacja została dodana pomyślnie!", "success");
      setShowConfirmation(false);
      eventBus.emit("recurring-reservation-created", {});
      navigate("/rezerwacje/sale-konferencyjne/");
    } catch (error) {
      Logger.error("Błąd podczas wysyłania rezerwacji:", error);
      // showNotification("Wystąpił błąd podczas wysyłania rezerwacji.", "error");
    } finally {
      setIsLoading(false); // Stop loading regardless of success or error
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const handleCancelChange = () => {
    navigate("/rezerwacje/sale-konferencyjne/");
  };

  const handleIconClick = () => {
    navigate("/rezerwacje/sale-konferencyjne/zarezerwuj");
  };

  // Dodajemy funkcję do renderowania modalu przez portal
  const renderConfirmationModal = () => {
    if (!showConfirmation) return null;

    return createPortal(
      <div className="confirmation-overlay">
        <div className="confirmation-box">
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt="Close"
            onClick={handleCancel}
          />
          <div className="areYouSure">
            <p>Czy na pewno chcesz zarezerwować salę?</p>
          </div>
          <div>
            <p>
              <strong>Sala: </strong>
              {confirmationData.car}
            </p>
            <p>
              <strong>Dzień tygodnia: </strong>
              {confirmationData.weekofDay}
            </p>
            <p>
              <strong>Godzina rozpoczęcia: </strong>
              {confirmationData.startHour}
            </p>
            <p>
              <strong>Godzina zakończenia: </strong>
              {confirmationData.endHour}
            </p>
            <p>
              <strong>Uczestnicy: </strong>
              {confirmationData.participants.join(", ")}
            </p>
            <p>
              <strong>Cel spotkania: </strong>
              {confirmationData.tripPurpose}
            </p>
          </div>
          <div className="confirmation-buttons">
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Tworzenie rezerwacji...</p>
              </div>
            ) : (
              <>
                <button onClick={handleConfirm}>TAK</button>
                <button onClick={handleCancelChange}>NIE</button>
              </>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  return (
    <div className="rooms-reservations-container">
      <div className="main-site">
        <h1>{t("reservations.rooms.recurringReservation")}</h1>
        <div
          className="orange-sort-icon"
          onClick={handleIconClick}
          onMouseEnter={() => setHoveredIcon(true)}
          onMouseLeave={() => setHoveredIcon(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hoveredIcon ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>

      <div className="date-inputs">
        <div className="the-first-line">
          <div className="input-group">
            <label htmlFor="days">Dzień tygodnia</label>
            <CustomDropdown
              options={options}
              value={selectedDay}
              onChange={handleChange}
              placeholder="Wybierz dzień tygodnia"
            />
            {errors.date && (
              <span className="error-message">{errors.date}</span>
            )}
          </div>

          <div className="input-group">
            <label htmlFor="startHour">Godzina rozpoczęcia</label>
            <DatePicker
              selected={startHour}
              onChange={handleStartHourChange}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Godzina"
              dateFormat="HH:mm"
              locale={pl}
              placeholderText="Wybierz godzinę"
              minDate={date ? date : new Date()}
              minTime={date ? startOfDay(date) : startOfDay(new Date())}
              maxTime={endHour ? endHour : endOfDay(date)}
              filterTime={(time) => isBefore(time, endHour) || !endHour}
              disabled={!date}
            />
            {errors.startHour && (
              <span className="error-message">{errors.startHour}</span>
            )}
          </div>

          <div className="input-group">
            <label htmlFor="endHour">Godzina zakończenia</label>
            <DatePicker
              selected={endHour}
              onChange={handleEndHourChange}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              timeCaption="Godzina"
              dateFormat="HH:mm"
              locale={pl}
              placeholderText="Wybierz godzinę"
              minDate={date ? date : new Date()}
              minTime={startHour ? startHour : new Date()}
              maxTime={endOfDay(date)}
              filterTime={(time) => isAfter(time, startHour)}
              disabled={!date}
            />
            {errors.endHour && (
              <span className="error-message">{errors.endHour}</span>
            )}
          </div>
        </div>

        {isDateAndTimeSet && (
          <div className="main">
            <div className="left-site">
              <div className="available-group">
                <h2>Sale konferencyjne:</h2>
                <div className="cars-list">
                  {rooms.map((room) => (
                    <div
                      key={room.id}
                      className="car-item"
                      onClick={() => handleCarSelection(room.name)}
                    >
                      <span
                        className={`car-checkbox ${
                          selectedCar === room.name ? "checked" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCarSelection(room.name);
                        }}
                      />
                      {room.name}
                    </div>
                  ))}
                </div>
                {errors.selectedCar && (
                  <span className="error-message">{errors.selectedCar}</span>
                )}
              </div>
            </div>

            <div className="right-site">
              <div className="input-group">
                <label htmlFor="participants">Uczestnicy</label>
                <select
                  id="newParticipant"
                  value={newParticipantId}
                  onChange={(e) => setNewParticipantId(e.target.value)}
                >
                  <option value="">Wybierz uczestnika</option>
                  {employees
                    .filter((employee) => employee.email !== getEmail())
                    .filter(
                      (employee) =>
                        !participants.some(
                          (participant) => participant.email === employee.email,
                        ),
                    )
                    .sort((a, b) => {
                      // Sortuj alfabetycznie według imienia i nazwiska
                      const nameA =
                        `${a.firstName} ${a.lastName}`.toLowerCase();
                      const nameB =
                        `${b.firstName} ${b.lastName}`.toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.firstName} {employee.lastName}
                      </option>
                    ))}
                </select>
                <button onClick={handleAddParticipant}>Dodaj uczestnika</button>
                {errors.participants && (
                  <span className="error-message">{errors.participants}</span>
                )}
                <ul className="participants-list">
                  {participants.map((participant, index) => (
                    <li key={index}>
                      <span>{participant.name}</span>
                      <img
                        src={deleteButtonIcon}
                        alt="Usuń"
                        onClick={() => handleRemoveParticipant(index)}
                        className="delete-button-icon"
                      />
                    </li>
                  ))}
                </ul>
              </div>

              <div className="input-group">
                <label htmlFor="tripPurpose">Cel spotkania</label>
                <textarea
                  id="tripPurpose"
                  value={tripPurpose}
                  onChange={(e) => setTripPurpose(e.target.value)}
                  placeholder="Podaj cel spotkania ..."
                />
                {errors.tripPurpose && (
                  <span className="error-message">{errors.tripPurpose}</span>
                )}
              </div>

              <button
                className="reservation-button"
                onClick={handleReservation}
              >
                Zarezerwuj salę
              </button>
            </div>
          </div>
        )}
      </div>

      {renderConfirmationModal()}
    </div>
  );
};

export default RecurringReservations;
